import type {
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  AxiosResponseHeaders,
} from "axios";

import {
  AttachmentType,
  EventTypeEnumCapitalizedType,
  MileageUnit,
  PermissionID,
} from "shared/types";

import type { ChipColor } from "features/components/Chip/Chip";

import client, {
  clientServerPydanticAI,
  clientV2,
  clientWithoutVersion,
} from "./axios";
import parseLinkHeader from "./parse-link-header";
import { createURL } from "./utils";
import { Vehicle } from "./vehicles/api";

export type APIHeaders = Record<string, string> & {
  link?: string;
};

export interface APIError {
  message: string;
  code: string;
  response?: {
    status: number;
    statusText: string;
    data: {
      code: string;
      message: string;
      details?: any;
    };
  };
}

export interface APIPaginatedRequest {
  limit?: number;
  before?: string;
  after?: string;
  filter?: string;
  sort?: string;
  reloadKey?: string;
}

interface APIListResponse<Data> {
  data: Data;
}

export interface APISuccessResponse<Data extends object> {
  data: Data;
  headers: AxiosResponseHeaders;
}

export interface APIErrorResponse {
  code: string;
  message: string;
  details?: any;
  response?: {
    status: number;
    statusText: string;
    data?: {
      code?: string;
      message?: string;
      details?: string | { reason: string };
    };
  };
}

export interface APIListValuesRequest {
  fieldName: string;
  like?: string;
  limit?: number;
  filter?: string;
  claimFilter?: string;
}

export interface APIListValuesResponse {
  fieldName: string;
  like?: string;
  distinctValues: (string | null)[];
}

export const getFetcher = <Data extends object>(
  path: string,
  requestConfig?: AxiosRequestConfig
): Promise<APISuccessResponse<Data>> =>
  client.get(path, requestConfig).then(manageFetcherResponse);

export const getFetcherV2 = <Data extends object>(
  path: string,
  requestConfig?: AxiosRequestConfig
): Promise<APISuccessResponse<Data>> =>
  clientV2.get(path, requestConfig).then(manageFetcherResponse);

// Used only to satisfy URL parsing for a relative request path.
const RELATIVE_URL_PARSE_BASE = "https://viaduct.local";

// The next page is the same request with the cursor the Link header hands back, kept relative or absolute as given.
const getNextPagePath = (path: string, link: unknown): string | undefined => {
  const after = parseLinkHeader(typeof link === "string" ? link : undefined)
    ?.next?.after;

  if (!after) return undefined;

  const url = new URL(path, RELATIVE_URL_PARSE_BASE);
  url.searchParams.set("after", after);

  return path.startsWith(url.origin)
    ? url.href
    : `${url.pathname}${url.search}`;
};

// Follows the Link header from page to page and returns every row as one list, for a resource a page needs whole
// rather than a page at a time. The headers returned are the last page's.
export const getAllPagesFetcherV2 = async <Item>(
  path: string
): Promise<APISuccessResponse<Item[]>> => {
  const items: Item[] = [];
  let headers = {} as AxiosResponseHeaders;
  let next: string | undefined = path;

  while (next) {
    const page: APISuccessResponse<Item[]> = await getFetcherV2<Item[]>(next);
    items.push(...page.data);
    headers = page.headers;
    next = getNextPagePath(next, page.headers.link);
  }

  return { data: items, headers };
};

export const getPydanticAIFetcher = <Data extends object>(
  path: string,
  requestConfig?: AxiosRequestConfig
): Promise<APISuccessResponse<Data>> =>
  clientServerPydanticAI.get(path, requestConfig).then(manageFetcherResponse);

export const postFetcher = <Data extends object>(
  path: string,
  data: object,
  headers?: AxiosRequestHeaders
): Promise<APISuccessResponse<Data>> =>
  client.post(path, data, { headers }).then(manageFetcherResponse);

export const postFetcherV2 = <Data extends object>(
  path: string,
  data: object,
  headers?: AxiosRequestHeaders
): Promise<APISuccessResponse<Data>> =>
  clientV2.post(path, data, { headers }).then(manageFetcherResponse);

export const patchFetcherV2 = <Data extends object>(
  path: string,
  data: object,
  requestConfig?: AxiosRequestConfig
): Promise<APISuccessResponse<Data>> =>
  clientV2.patch(path, data, requestConfig).then(manageFetcherResponse);

export const patchFetcher = <Data extends object>(
  path: string,
  data: object,
  headers?: AxiosRequestHeaders
): Promise<APISuccessResponse<Data>> =>
  client.patch(path, data, { headers }).then(manageFetcherResponse);

export const putFetcher = <Data extends object>(
  path: string,
  data: object,
  headers?: AxiosRequestHeaders
): Promise<APISuccessResponse<Data>> =>
  client.put(path, data, { headers }).then(manageFetcherResponse);

const manageFetcherResponse = <Data extends object>({
  data,
  headers,
}: AxiosResponse<Data | APIListResponse<Data>>): APISuccessResponse<Data> => {
  // always flatten API responses with data as the ONLY top-level field
  // Typescript respects the "in" to understand data is type APIListResponse<Data>
  // - also check if data is not "empty/null" first, as some PATCH request can return 204 - No Content
  if (data && "data" in data && Object.keys(data).length === 1) {
    return {
      data: data.data,
      headers: headers as AxiosResponseHeaders,
    };
  }

  return {
    data: data as Data,
    headers: headers as AxiosResponseHeaders,
  };
};

export interface ListAttributesRequest {
  filter?: string;
}

// Vehicles ECUs
export interface VehicleECU {
  ECUID: string;
  VIN: string;
  createdAt: string;
  updatedAt: string;
  calibration: string | null;
  configuration: string | null;
  hardware: string | null;
  program: string | null;
  serialNumber: string | null;
  software: string | null;
  supplier: string | null;
}

export const listVehiclesECUsRequestURI = ({
  ...params
}: APIPaginatedRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["vehicleECUs"]),
    params,
  });

// Vehicles Options
export interface VehicleOption {
  optionID: string;
  VIN: string;
  createdAt: string;
  updatedAt: string;
  value: string | null;
}

// Vehicles Parts
export interface VehiclePart {
  partID: string;
  VIN: string;
  createdAt: string;
  updatedAt: string;
  totalQuantityOrdered: number | null;
  totalQuantityCommited: number | null;
  part: Part;
}

export const listVehiclesOptionsRequestURI = ({
  ...params
}: APIPaginatedRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["vehicleOptions"]),
    params,
  });

export const listVehiclesPartsRequestURI = ({
  ...params
}: APIPaginatedRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["vehicleParts"]),
    params,
  });

export interface CountResponse {
  count: number;
}

export interface CountRequest {
  filter?: string;
  analytics?: boolean;
}

// ---------
// Export CSV / EXCEL (download)
// ---------
export type ExportFileType = "xlsx" | "csv";

export interface ExportRequest extends APIPaginatedRequest {
  type: ExportFileType;
  // we need this for export URLs that need an ID in between, like issues/:ID/signalEvents/export/xlsx
  IDs: string[];
}

// ---------
// MAINTENANCE SCHEDULES + Vehicle Categories & Transport Categories
// ---------

export interface MaintenanceSchedule {
  service: string;
  miles?: number;
  kilometers?: number;
  hours?: number;
  months?: number;
}

export interface GetMaintenanceScheduleRequest extends APIPaginatedRequest {}

export const listMaintenanceScheduleRequestURI = ({
  ...params
}: GetMaintenanceScheduleRequest) =>
  client.getUri({
    method: "get",
    url: `/maintenanceScheduleServices`,
    params,
  });

// Transport Categories

// used for both Vehicle Category & Transport Category
export interface VehicleGenericCategory {
  ID: string;
  name: string;
  description?: string;
}

export interface GetApiIdRequest {
  id: string;
}

export const getTransportCategoryRequestURI = ({ id }: GetApiIdRequest) =>
  client.getUri({
    method: "get",
    url: createURL(["transportCategories", id]),
  });

export const getVehicleCategoryRequestURI = ({ id }: GetApiIdRequest) =>
  client.getUri({
    method: "get",
    url: createURL(["vehicleCategories", id]),
  });

export const getTransportCategory = (args: GetApiIdRequest) =>
  getFetcher<VehicleGenericCategory>(getTransportCategoryRequestURI(args));

export const listTransportCategoriesRequestURI = () =>
  client.getUri({
    method: "get",
    url: `/transportCategories`,
  });

export const listTransportCategories = () =>
  getFetcher<VehicleGenericCategory[]>(listTransportCategoriesRequestURI());

// Vehicle Categories
export const listVehicleCategoriesRequestURI = (): string =>
  client.getUri({
    method: "get",
    url: `/vehicleCategories`,
  });

// ---------
// Fleets
// ---------
export interface ListFleetsRequest extends APIPaginatedRequest {}

export interface Fleet {
  name: string;
  numClaimsLastPeriod: number;
  numVehicles: number;
  percentageClaimsLastPeriod: number;
  timePeriodDays: number;
}

// How a dynamic schema attribute's value is displayed: the admin's stored keys merged over the defaults for the
// column's data type, so readers never fill defaults. Which member applies is decided by the column data type, not by
// the object's shape. Keys with no default (compactDisplay, prefix, suffix, chipColors) appear only when set.

// The Intl.NumberFormat constructor options, limited to the values the API accepts, plus Viaduct's own prefix and
// suffix. The web passes every key except prefix and suffix straight to `new Intl.NumberFormat(locale, options)`.
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
export interface NumberFormatOptions {
  notation: Extract<
    Intl.NumberFormatOptions["notation"],
    "standard" | "compact"
  >;
  compactDisplay?: Intl.NumberFormatOptions["compactDisplay"];
  useGrouping: Intl.ResolvedNumberFormatOptionsUseGrouping;
  minimumFractionDigits: number;
  maximumFractionDigits: number;
  prefix?: string | null;
  suffix?: string | null;
  // Int columns only.
  chipColors?: ChipColorRule[];
}

export interface DateFormatOptions {
  // A date template such as "MM/dd/yyyy". The web localizes the templates that match its display date formats
  // through Intl and renders any other one with date-fns, so the API does not depend on the web's constants.
  dateFormat: string;
}

export interface BooleanFormatOptions {
  style: "icon" | "text";
  trueLabel: string | null;
  falseLabel: string | null;
  chipColors?: ChipColorRule[];
}

export interface StringFormatOptions {
  underscoresAsSpaces: boolean;
  chipColors?: ChipColorRule[];
}

export interface JsonFormatOptions {
  pointer: string | null;
}

export interface StringArrayFormatOptions {
  separator: string;
  style: "text" | "chips";
  maxItems: number | null;
}

// A value shows in a chip with the color of the rule whose value is equal to it, or in the default color.
export interface ChipColorRule {
  value: number | boolean | string;
  color: ChipColor;
}

export type AttributeFormat =
  | NumberFormatOptions
  | DateFormatOptions
  | BooleanFormatOptions
  | StringFormatOptions
  | JsonFormatOptions
  | StringArrayFormatOptions;

export interface EntityAttribute {
  ID: string;
  displayName: string;
  columnName: string | null;
  dataExplorerColumnName: string | null;
  description: string | null;
  type: string;
  nullable: boolean;
  filtering: boolean;
  grouping: boolean;
  attributeGrouping: boolean;
  sorting: boolean;
  values: boolean;
  // endpoint where details of the parameter can be retrieved
  relationEndpoint?: string | null;
  // the ID to use when expanding relation on relationEndpoint
  relationEndpointIDColumn?: string | null;
  filteringConfig: EntityAttributeFiltering;
  // endpoint returns this field in response. If set to false, it is only available for filtering
  inJSONBody: boolean;
  // draws wider column in table (w-80)
  displayWideColumn: boolean;
  // will prevent showing this attribute in tables, but keep displaying it elsewhere (ie. filters, grouping ..)
  hideInTable: boolean;
  // hides the attribute's filter (in top-level filters & inside tables)
  hideFilter: boolean;
  byVehicleAgeBirthday: boolean;
  byVehicleAgeExposure: boolean;
  byVehicleAgeExposureBuckets: number[];
  columnWidth: number | null;
  unitAtRest?: string | null;
  unitType?: string | null;
  unit?: string | null;
  export?: boolean;
  globalSearch?: boolean;
  textSearchable?: boolean;
  // Server-generated/derived attribute (for example attachment virtual fields).
  virtual?: boolean;
  // Absent from an API that predates it and from attributes with no format capability (relations, virtual
  // attributes, static resources).
  format?: AttributeFormat;
}

interface EntityAttributeFiltering {
  negativeNumbers: boolean;
  decimalNumbers: boolean;
  contains: boolean;
  empty: boolean;
  startsWith: boolean;
  // indicates that there is a low number of distinct values, suggesting that we load them on open
  lowCardinality: boolean;
  // enables support for min/max filtering in "IN" / "NOT_IN" filters on date or numeric attributes
  minMax: boolean;
}

export interface SmartGroup {
  ID: string;
  name: string;
  description: string;
  eventType: string;
  createdAt: string;
  updatedAt: string;
}

export const listFleetsRequestURI = ({
  ...params
}: ListFleetsRequest): string =>
  client.getUri({
    method: "get",
    url: `/fleets`,
    params,
  });

export const listFleetCountRequestURI = ({ ...params }: CountRequest): string =>
  client.getUri({
    method: "get",
    url: `/fleets/count`,
    params,
  });

const listVehicleTagsValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["vehicleTags", "values", fieldName]),
    params,
  });

export const listVehicleTagsValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listVehicleTagsValuesRequestURI(args));

const listVehicleECUsValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["vehicleECUs", "values", fieldName]),
    params,
  });

export const listVehicleECUsValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listVehicleECUsValuesRequestURI(args));

const listVehicleOptionsValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["vehicleOptions", "values", fieldName]),
    params,
  });

export const listVehicleOptionsValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listVehicleOptionsValuesRequestURI(args));

const listVehiclePartsValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["vehicleParts", "values", fieldName]),
    params,
  });

export const listVehiclePartsValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listVehiclePartsValuesRequestURI(args));

const listManufactureLineExitsValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["vehicles", "manufactureLineExits", "values", fieldName]),
    params,
  });

export const listManufactureLineExitsValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(
    listManufactureLineExitsValuesRequestURI(args)
  );

// ---------
// Healthcheck
// ---------
export interface Healthcheck {
  instance: string;
  service: string;
  version: string;
}

export const getHealthcheckRequestURI = () =>
  clientWithoutVersion.getUri({
    method: "get",
    url: "/healthcheck",
  });

// generic request definitions
export interface GetRequest {
  id: string;
}

export interface DeleteRequest {
  id: string;
}

export interface Dealer {
  ID: string;
  name: string;
  createdAt: string;
  city: string | null;
  countryCode: string | null;
  provinceCode: string | null;
  updatedAt: string;
}

interface GroupData {
  name: string;
  description: string;
}

export interface PermissionEntry {
  ID: string;
  email?: string;
  everyone?: boolean;
  access: PermissionID;
  originalAccess?: PermissionID;
  // The API returns null on user grants, a group id string on group grants.
  groupID?: string | null;
  group?: GroupData;
  shown?: boolean;
  // Client-side row key for the permissions dialog. Unsaved draft rows have no server `ID`, so this
  // keeps them distinct in the grid and in the edit handlers; it is never sent to the API.
  rowId?: string;
}

export interface CreatePermissionEntry {
  email?: string;
  everyone?: boolean;
  access: PermissionID;
}

interface UpdatePermissionEntry {
  ID: string;
  access: PermissionID;
}

export interface UpdatePermissionRequest {
  create: CreatePermissionEntry[];
  update: UpdatePermissionEntry[];
  remove: string[];
}

export interface DescriptionObject {
  ID: string;
  description: string;
}

// ---------
// Labor codes
// ---------

export interface LaborCode extends DescriptionObject {}

const LABOR_CODES_BASE_ROUTE = "laborCodes";

export interface ListLaborCodesRequest extends APIPaginatedRequest {}

export const listLaborCodesURI = (params: ListLaborCodesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([LABOR_CODES_BASE_ROUTE]),
    params,
  });

export const listLaborCodes = (args: ListLaborCodesRequest) =>
  getFetcher<LaborCode[]>(listLaborCodesURI(args));

interface GetLaborCodeRequest {
  id: string;
}

const getLaborCodeURI = ({ id }: GetLaborCodeRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([LABOR_CODES_BASE_ROUTE, id]),
  });

export const getLaborCode = (args: GetLaborCodeRequest) =>
  getFetcher<LaborCode>(getLaborCodeURI(args));

// ---------
// Parts
// ---------

export interface Part extends DescriptionObject {
  systemCode: string | null;
  assemblyCode: string | null;
  componentCode: string | null;
}

const PARTS_BASE_ROUTE = "parts";

export interface ListPartsRequest extends APIPaginatedRequest {}

export const listPartsURI = (params: ListPartsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([PARTS_BASE_ROUTE]),
    params,
  });

export const listParts = (args: ListPartsRequest) =>
  getFetcher<Part[]>(listPartsURI(args));

interface GetPartRequest {
  id: string;
}

const getPartURI = ({ id }: GetPartRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([PARTS_BASE_ROUTE, id]),
  });

export const getPart = (args: GetPartRequest) =>
  getFetcher<Part>(getPartURI(args));

export interface ECU {
  ID: string;
  description: string;
  status: string;
}

export const listECUsRequestURI = (params: APIPaginatedRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["ECUs"]),
    params,
  });

export interface Option {
  ID: string;
  description: string;
}

export const listOptionsRequestURI = (params: APIPaginatedRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["options"]),
    params,
  });

export interface VehicleAgeTimeline {
  birthdayBucket: string;
  exposureBucket: number;

  numVehicles: number;
  numEvents: number;
  cumulativeIPTV: number | null;
  cumulativeCPV?: number; // claims only
  distinctIPTV?: number; // claims
  vehiclesAtExposure?: number; // claims
  percentVehiclesAtExposure?: number; // claims
  totalVehicles?: number; // claims
  cumulativeDPTV?: number; // signal events
  costRate?: number;
  FRCPercent?: number;
  eventsPerUnit?: number;
}

export interface ByVehicleAgeAgeRequest {
  byVehicleAgeBirthday: string;
  byVehicleAgeExposure: string;
  granularity: string;
}

export interface TopContributorsAgeRequest {
  byVehicleAgeExposure?: string;
  byVehicleAgeExposureBucket?: number;
}

export interface EventType {
  type: EventTypeEnumCapitalizedType;
  name: string;
  resourceEndpoint: string;
  attributeEndpoint: string;
  VINAttribute: string;
  dateAttribute: string;
  mileageAttribute: string;
  engineHoursAttribute: string;
}

export const listEventRegistryURI = (): string =>
  client.getUri({
    method: "get",
    url: createURL(["eventRegistry"]),
  });

export interface AssociatedVehicle extends Vehicle {
  numRelatedOccurrences: number;
  lastRelatedOccurrenceAt: string;
  firstRelatedOccurrenceAt: string;
}

export interface ListEventsRequest extends APIPaginatedRequest {
  mileageUnit?: MileageUnit;
  vehiclesFilter?: string;
  assetsFilter?: string;
  skipRequest?: boolean;
  // not relevant for signal events
  analytics?: boolean;
  returnVehicles?: boolean;
  returnMatchedEventCounts?: boolean;
  returnPhotos?: boolean;
  returnSmartGroups?: boolean;
  returnAttachments?: boolean;
  includeSrcURLs?: boolean;
}

export interface AssociatedVehicleTimelineEvent {
  id: string;
  date: string;
  eventType: string;
  daysSinceOccursDate: number;
  occurrenceCount: number;
}

export interface AssociatedVehicleTimelineEvents {
  vin: string;
  events: AssociatedVehicleTimelineEvent[];
  overlayEvents: AssociatedVehicleTimelineEvent[];
  referenceDate: string;
}

export interface AssociatedVehicleTimelineRequest {
  filter: string;
  overlayEvent: string;
  overlayEventFilter: string;
  occursBefore: number;
  occursAfter: number;
  occursZeroDate: string;
  applyOccursToOverlayEvents: boolean;
  limit: number;
}

export interface GenericAPIListValuesRequest extends APIListValuesRequest {
  resourceEndpoint: string;
}

const listGenericValues = (args: GenericAPIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listGenericValuesRequestURI(args));

const listGenericValuesRequestURI = ({
  resourceEndpoint,
  fieldName,
  ...params
}: GenericAPIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([resourceEndpoint, "values", fieldName]),
    params,
  });

const listGenericValuesV2 = (args: GenericAPIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listGenericValuesV2RequestURI(args));

export const listGenericValuesV2RequestURI = ({
  resourceEndpoint,
  fieldName,
  ...params
}: GenericAPIListValuesRequest): string =>
  clientV2.getUri({
    method: "get",
    url: createURL([...resourceEndpoint.split("/"), "values", fieldName]),
    params,
  });

export const genericValuesCallback =
  (valuesEndpoint: string) =>
  ({
    fieldName,
    like,
  }: Omit<GenericAPIListValuesRequest, "resourceEndpoint">) => {
    if (valuesEndpoint.startsWith("v2/")) {
      const resourceEndpoint = valuesEndpoint.replace("v2/", "");

      return listGenericValuesV2({
        resourceEndpoint,
        fieldName,
        like,
      });
    }

    return listGenericValues({
      resourceEndpoint: valuesEndpoint,
      fieldName,
      like,
    });
  };

export interface Attachment {
  ID: string;
  storageKey: string;
  filename: string;
  contentType: string | null;
  createdAt: string;
  updatedAt: string;
  uploadedAt: string | null;
  url: string | null;
}

export interface DownloadAllAttachmentsRequest {
  resource: AttachmentType;
  id: string;
}
